import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extractErrorMessage } from '@/utils/format';
import {
  Box,
  Button,
  Stack,
  Dialog,

  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Typography,
} from '@mui/material';
import { CheckCircle as CheckIcon, Warning as WarningIcon } from '@mui/icons-material';
import { DataGrid, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { Edit as EditIcon } from '@mui/icons-material';
import StatusDot from '@/components/common/StatusDot';
import BrandedDialogTitle from '@/components/common/BrandedDialogTitle';
import { settingsApi } from '@/api/endpoints/settings';
import { useNotificationStore } from '@/stores/notificationStore';
import { usePermission } from '@/hooks/usePermission';
import PageHelpIcon from '@/components/PageHelpIcon';
import { TYPE } from '@/theme/typography';
const CATEGORIES: { value: string; label: string }[] = [
  { value: 'revenue_type', label: 'Revenue Type' },
  { value: 'expense_category', label: 'Expense Category' },
  { value: 'payment_method', label: 'Payment Method' },
  { value: 'department', label: 'Department' },
  { value: 'leave_type', label: 'Leave Type' },
  { value: 'allowance_type', label: 'Allowance Type' },
  { value: 'violation_category', label: 'Violation Category' },
  { value: 'room_type', label: 'Room Type' },
];

const LEAVE_TEMPLATES = {
  minimum: [
    { code: 'ANNUAL', name: 'Annual Leave', days: 6 },
    { code: 'SICK', name: 'Sick Leave', days: 12 },
  ],
  standard: [
    { code: 'ANNUAL', name: 'Annual Leave', days: 15 },
    { code: 'SICK', name: 'Sick Leave', days: 12 },
    { code: 'MATERNITY', name: 'Maternity Leave', days: 12 },
  ],
  comprehensive: [
    { code: 'ANNUAL', name: 'Annual Leave', days: 21 },
    { code: 'SICK', name: 'Sick Leave', days: 12 },
    { code: 'MATERNITY', name: 'Maternity Leave', days: 12 },
    { code: 'PATERNITY', name: 'Paternity Leave', days: 10 },
    { code: 'COMPASSIONATE', name: 'Compassionate Leave', days: 3 },
  ],
};

function validateLeaveCompliance(leaves: any[]) {
  const codes = leaves.map((l: any) => (l.code || '').toUpperCase());
  const hasAnnual = codes.some((c: string) => c === 'ANNUAL');
  const hasSick = codes.some((c: string) => c === 'SICK');

  const warnings: string[] = [];
  if (!hasAnnual) warnings.push('Missing Annual Leave (Labour Act requires minimum 6 days)');
  if (!hasSick) warnings.push('Missing Sick Leave (Labour Act requires minimum 12 days)');

  return warnings;
}

export default function ReferenceDataPage({ embedded }: { embedded?: boolean }) {
  const { hasPermission } = usePermission();
  const [selectedCategory, setSelectedCategory] = useState<string>('revenue_type');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    category: 'revenue_type',
    code: '',
    name: '',
    sort_order: 0,
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ code?: string; name?: string }>({});
  const { showSuccess } = useNotificationStore();
  const queryClient = useQueryClient();

  const { data: refData, isLoading, error: queryError } = useQuery({
    queryKey: ['settings/reference-data', selectedCategory],
    queryFn: () => settingsApi.listReferenceData(selectedCategory),
    select: (r) => r.data,
  });

  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => settingsApi.createReferenceData(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/reference-data'] });
      handleCloseDialog();
      showSuccess('Reference data created successfully');
    },
    onError: (err: any) => {
      setError(extractErrorMessage(err, 'Failed to create reference data. Please try again.'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      settingsApi.updateReferenceData(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings/reference-data'] });
      handleCloseDialog();
      showSuccess('Reference data updated successfully');
    },
    onError: (err: any) => {
      setError(extractErrorMessage(err, 'Failed to update reference data.'));
    },
  });

  const handleEdit = (row: any) => {
    setEditingId(row.id);
    setFormData({
      category: row.category || selectedCategory,
      code: row.code,
      name: row.name,
      sort_order: row.sort_order ?? 0,
    });
    setOpenDialog(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    const errs: { code?: string; name?: string } = {};
    if (!formData.code.trim()) errs.code = 'Code is required';
    if (!formData.name.trim()) errs.name = 'Name is required';
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;
    if (editingId) {
      updateMutation.mutate({ id: editingId, data: { code: formData.code, name: formData.name, sort_order: formData.sort_order } });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingId(null);
    setFormData({
      category: selectedCategory,
      code: '',
      name: '',
      sort_order: 0,
    });
    setError('');
    setFieldErrors({});
  };

  const columns: GridColDef[] = [
    { field: 'code', headerName: 'Code', flex: 0.5, minWidth: 80 },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'sort_order', headerName: 'Order', flex: 0.3, minWidth: 60, align: 'right', headerAlign: 'right' },
    {
      field: 'is_active',
      headerName: 'Status',
      flex: 0.4,
      minWidth: 70,
      renderCell: (params) => <StatusDot active={params.value} />,
    },
    ...(hasPermission('settings.company.write') ? [{
      field: 'actions',
      type: 'actions' as const,
      headerName: '',
      width: 50,
      maxWidth: 50,
      getActions: (params: any) => [
        <GridActionsCellItem
          key="edit"
          icon={<EditIcon fontSize="small" />}
          label="Edit"
          onClick={() => handleEdit(params.row)}
        />,
      ],
    }] : []),
  ];

  if (isLoading) {
    return (
      <Box sx={{ p: embedded ? 0 : 3, display: 'flex', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" gap={1}>
          <FormControl sx={{ minWidth: 240 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              label="Category"
              size="small"
            >
              {CATEGORIES.map(cat => (
                <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <PageHelpIcon pageKey="settings.reference-data" />
        </Stack>
        {hasPermission('settings.company.write') && (
          <Button variant="contained" onClick={() => setOpenDialog(true)}>
            Add Entry
          </Button>
        )}
      </Stack>

      {queryError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load reference data. Please refresh the page.
        </Alert>
      )}

      {/* Leave Type Compliance Card */}
      {selectedCategory === 'leave_type' && (
        <Card sx={{ mb: 2, bgcolor: 'info.50' }}>
          <CardContent>
            <Stack spacing={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <WarningIcon sx={{ fontSize: 18 }} /> Nigerian Labour Act Compliance
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Minimum leave entitlements per Labour Act 2004: Annual 6 days, Sick 12 days, Maternity 12 weeks
                </Typography>

                {/* Compliance Warnings */}
                {validateLeaveCompliance(refData || []).map((warning, i) => (
                  <Alert key={i} severity="warning" sx={{ mb: 1, py: 0.5 }}>
                    {warning}
                  </Alert>
                ))}

                {validateLeaveCompliance(refData || []).length === 0 && (
                  <Alert severity="success" sx={{ mb: 1, py: 0.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckIcon sx={{ fontSize: 16 }} /> Your leave types meet Labour Act requirements
                  </Alert>
                )}
              </Box>

              {/* Template Buttons */}
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 'bold', textTransform: 'uppercase' }}>
                  Quick Apply Template:
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {['minimum', 'standard', 'comprehensive'].map((templateKey) => (
                    <Button
                      key={templateKey}
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        const template = LEAVE_TEMPLATES[templateKey as keyof typeof LEAVE_TEMPLATES];
                        // Use bulk create if available, otherwise create one-by-one
                        template.forEach((item, idx) => {
                          const data = {
                            category: 'leave_type',
                            code: item.code,
                            name: item.name,
                            sort_order: idx,
                          };
                          settingsApi.createReferenceData(data).catch(() => {
                            // Handle duplicate or other errors silently (template may have some items already)
                          });
                        });
                        queryClient.invalidateQueries({ queryKey: ['settings/reference-data'] });
                        showSuccess(`Applied ${templateKey.replace(/([A-Z])/g, ' $1')} template`);
                      }}
                      sx={{ textTransform: 'none', fontWeight: TYPE.medium }}
                    >
                      {templateKey === 'minimum' && 'Government Minimum'}
                      {templateKey === 'standard' && 'Best Practice'}
                      {templateKey === 'comprehensive' && 'Comprehensive'}
                    </Button>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      <Box>
          <DataGrid
            autoHeight
            density="compact"
            rows={refData ?? []}
            columns={columns}
            disableRowSelectionOnClick
            pageSizeOptions={[10, 25, 50]}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              sorting: { sortModel: [{ field: 'sort_order', sort: 'asc' }] },
            }}
            sx={{ border: 'none' }}
            localeText={{ noRowsLabel: 'No reference data entries for this category.' }}
          />
      </Box>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <BrandedDialogTitle>{editingId ? 'Edit Reference Data' : 'Add Reference Data'}</BrandedDialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
          <form>
            <FormControl fullWidth margin="normal">
              <InputLabel>Category</InputLabel>
              <Select
                value={formData.category}
                onChange={(e) => setFormData({...formData, category: e.target.value})}
                label="Category"
              >
                {CATEGORIES.map(cat => (
                  <MenuItem key={cat.value} value={cat.value}>{cat.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Code"
              value={formData.code}
              onChange={(e) => { setFormData({...formData, code: e.target.value}); setFieldErrors((p) => ({ ...p, code: undefined })); }}
              margin="normal"
              required
              error={!!fieldErrors.code}
              helperText={fieldErrors.code}
            />
            <TextField
              fullWidth
              label="Name"
              value={formData.name}
              onChange={(e) => { setFormData({...formData, name: e.target.value}); setFieldErrors((p) => ({ ...p, name: undefined })); }}
              margin="normal"
              required
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
            />
            <TextField
              fullWidth
              label="Sort Order"
              type="number"
              value={formData.sort_order}
              onChange={(e) => setFormData({...formData, sort_order: parseInt(e.target.value)})}
              margin="normal"
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            {(createMutation.isPending || updateMutation.isPending) ? 'Saving...' : (editingId ? 'Save Changes' : 'Add Entry')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
